"use client";

import { Database, Bell, Radio, Shield, Zap } from "lucide-react";

export function HowItWorksSection() {
  const features = [
    {
      icon: Database,
      title: "24/7 Monitoring",
      description: "Our system continuously scans multiple databases, communities, and platforms where music leaks typically surface first."
    },
    {
      icon: Zap,
      title: "Instant Detection",
      description: "Advanced algorithms detect new leaks within seconds of them appearing, ensuring you're among the first to know."
    },
    {
      icon: Bell,
      title: "Real-Time Alerts",
      description: "Get immediate notifications through your preferred channels - SMS, email, or Discord - the moment new tracks are detected."
    },
    {
      icon: Shield,
      title: "Verified Sources",
      description: "Our system filters out fake leaks and ensures you only receive alerts for legitimate unreleased tracks."
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-black relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-black to-black pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-purple-400 mb-6">
            How It Works
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Our advanced monitoring system keeps you ahead of the curve, ensuring you never miss another leaked track from your favorite artists.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="relative group p-6 bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800/50 hover:border-purple-500/50 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* System Overview */}
        <div className="mt-20 text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6">
            Comprehensive Monitoring System
          </h3>
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800/50 p-6 sm:p-8 max-w-3xl mx-auto">
            <p className="text-gray-300 leading-relaxed mb-6">
              Our sophisticated system maintains constant surveillance across various platforms where unreleased music typically emerges. From private communities to public databases, we track and verify potential leaks in real-time.
            </p>
            <div className="space-y-4 text-left">
              <div className="flex items-center gap-3">
                <Radio className="w-5 h-5 text-purple-400" />
                <span className="text-gray-300">Continuous scanning of known leak sources</span>
              </div>
              <div className="flex items-center gap-3">
                <Radio className="w-5 h-5 text-purple-400" />
                <span className="text-gray-300">Verification of audio authenticity</span>
              </div>
              <div className="flex items-center gap-3">
                <Radio className="w-5 h-5 text-purple-400" />
                <span className="text-gray-300">Instant notification dispatch to subscribers</span>
              </div>
              <div className="flex items-center gap-3">
                <Radio className="w-5 h-5 text-purple-400" />
                <span className="text-gray-300">24/7 system uptime with redundant monitoring</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 